import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

import { PreferencesDialog } from "./PreferencesDialog";

interface PreferencesDialogContextValue {
  /** Opens the user's Preferences dialog. */
  open: () => void;
}

const PreferencesDialogContext = createContext<PreferencesDialogContextValue | null>(null);

/**
 * Holds the Preferences dialog above the page, at the shell. The shell keys
 * the open page on the reader's units so a unit change repaints it, which
 * remounts everything inside the page — on the dashboard launcher that
 * includes the account menu, and a dialog owned there closed on every unit
 * pick. Owned here, it stays open while the page behind it repaints.
 */
export function PreferencesDialogProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);
  const value = useMemo(() => ({ open }), [open]);
  return (
    <PreferencesDialogContext.Provider value={value}>
      {children}
      <PreferencesDialog open={isOpen} onOpenChange={setIsOpen} />
    </PreferencesDialogContext.Provider>
  );
}

/**
 * The shell's Preferences dialog, or `null` outside the shell (the design
 * system page), where a caller keeps a dialog of its own.
 */
export function usePreferencesDialog(): PreferencesDialogContextValue | null {
  return useContext(PreferencesDialogContext);
}
